import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { walkInService, BarberService } from '../../services/walkInService';
import { useToast } from '../../context/ToastContext';
import BarberHeader from '../../components/barber/BarberHeader';

interface LocationState {
    clientId: number | null;
    clientName: string | null;
    isAnonymous: boolean;
    startDate?: string;
    startTime?: string;
}

const WalkInSelectServicePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const [services, setServices] = useState<BarberService[]>([]);
    const [promotions, setPromotions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const state = location.state as LocationState;

    // Redirect if no state
    useEffect(() => {
        if (!state) {
            navigate('/barber/walk-in');
        } else {
            loadServices();
        }
    }, [state, navigate]);

    const loadServices = async () => {
        try {
            setLoading(true);
            const [barberServices, activePromotions] = await Promise.all([
                walkInService.getServices(),
                walkInService.getActivePromotions().catch(() => []) // Catch error if any
            ]);
            
            setServices(barberServices);
            
            // Filter promotions that match the barber's active services
            const availablePromos = activePromotions.filter(promo => 
                barberServices.some(s => s.serviceTypeId === promo.serviceTypeId)
            );
            setPromotions(availablePromos);
            
        } catch (error) {
            console.error('Error loading services:', error);
            showToast('Error al cargar servicios', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleServiceSelect = (service: BarberService) => {
        navigate('/barber/walk-in/confirm', {
            state: {
                ...state,
                serviceId: service.id,
                serviceName: service.serviceName,
                servicePrice: service.price,
                serviceDuration: service.durationMinutes
            }
        });
    };

    const handlePromotionSelect = (promo: any) => {
        const matchingService = services.find(s => s.serviceTypeId === promo.serviceTypeId);
        const baseDuration = matchingService ? matchingService.durationMinutes : 30;

        navigate('/barber/walk-in/confirm', {
            state: {
                ...state,
                promotionId: promo.id,
                serviceName: promo.name,
                servicePrice: promo.price,
                serviceDuration: promo.multiplier * baseDuration,
                isPromotion: true,
                multiplier: promo.multiplier
            }
        });
    };

    const getClientDisplayName = () => {
        if (state?.clientName) {
            return state.clientName;
        }
        return 'Cliente Walk-in';
    };

    if (!state) {
        return null;
    }

    return (
        <>
            <BarberHeader
                title="🚶 Atender Ahora"
                subtitle="Paso 2: Seleccionar servicio"
                rightContent={
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate('/barber/dashboard')}
                    >
                        ✕ Cancelar
                    </button>
                }
            />
            <div className="container fade-in" style={{ maxWidth: '600px', margin: '0 auto', padding: '1rem' }}>

                {/* Step indicator */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--success)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>✓</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--success)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--accent)', color: 'var(--text-on-accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>2</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--secondary)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--secondary)', color: 'var(--text-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>3</div>
                </div>

                {/* Back button */}
                <button
                    onClick={() => navigate('/barber/walk-in')}
                    style={{
                        marginBottom: '1rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                >
                    ← Volver
                </button>

                {/* Client info */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    borderRadius: '0.75rem',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    color: 'var(--text-primary)'
                }}>
                    <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Cliente:</span>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem', marginTop: '0.25rem' }}>
                        👤 {getClientDisplayName()}
                        {state.isAnonymous && (
                            <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                color: 'var(--warning)',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '1rem',
                                fontWeight: '600'
                            }}>WALK-IN</span>
                        )}
                    </div>
                </div>

                <h2 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>✂️ Seleccionar servicio</h2>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        Cargando servicios...
                    </div>
                ) : services.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        backgroundColor: 'var(--surface)',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--secondary)',
                        color: 'var(--text-secondary)'
                    }}>
                        No hay servicios disponibles
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {services.map(service => (
                            <div
                                key={service.id}
                                onClick={() => handleServiceSelect(service)}
                                style={{
                                    padding: '1.25rem',
                                    backgroundColor: 'var(--surface)',
                                    border: '2px solid var(--secondary)',
                                    borderRadius: '0.75rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'all 0.2s',
                                    color: 'var(--text-primary)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(190, 242, 100, 0.1)';
                                    e.currentTarget.style.borderColor = 'var(--accent)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--surface)';
                                    e.currentTarget.style.borderColor = 'var(--secondary)';
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>{service.serviceName}</div>
                                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                        ⏱️ {service.durationMinutes} minutos
                                    </div>
                                </div>
                                <div style={{ fontWeight: '800', color: 'var(--accent)', fontSize: '1.25rem' }}>
                                    ${service.price.toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Promotions section */}
                {!loading && promotions.length > 0 && (
                    <>
                        <h2 style={{ marginTop: '2.5rem', marginBottom: '1rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>🎁</span> Promociones Especiales
                        </h2>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {promotions.map(promo => (
                                <div
                                    key={`promo-${promo.id}`}
                                    onClick={() => handlePromotionSelect(promo)}
                                    style={{
                                        padding: '1.25rem',
                                        backgroundColor: 'rgba(245, 158, 11, 0.05)',
                                        border: '2px solid rgba(245, 158, 11, 0.3)',
                                        borderRadius: '0.75rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'all 0.2s',
                                        color: 'var(--text-primary)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.15)';
                                        e.currentTarget.style.borderColor = 'var(--accent)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.05)';
                                        e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: '700', fontSize: '1.1rem', color: 'var(--accent)' }}>{promo.name}</div>
                                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                            ⏱️ {promo.serviceTypeName} (x{promo.multiplier})
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: '800', color: 'var(--accent)', fontSize: '1.25rem' }}>
                                        ${promo.price.toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default WalkInSelectServicePage;
